// For n-ary tree with in branch array 'children' and elements to check in an
// array 'containerArray' stored in the node
// e.g. nodes like this
// {
//   files: [file1,file2,...],
//   children: [branchnode1, branchnode2, branchnode3,....]
// }
// tree is pointer to a node
// children = 'children'
// containerArray = 'files'
// filter is a function takes a node object and interrogates it in some way

// Unit test status: TESTED
/* eslint-disable no-unused-vars */
/* eslint-disable no-shadow */
/* eslint-disable no-param-reassign */
function findFirstTreeNode(tree, children, containerArray, filter) {
  let found = false;

  if (tree[containerArray]) {
    //    if (Array.isArray(tree[containerArray]))
    //      found = tree[containerArray].find(tce => filter(tce))
    //    else
    found = filter(tree) ? tree : false;
  }

  if (!found) {
    if (tree[children]) {
      tree[children].some((tc) => {
        const result = findFirstTreeNode(tc, children, containerArray, filter);
        if (result) found = result;
        return result;
      });
    }
  }
  return found;
}
// Top down filter for an n-ary tree, parent nodes prioritised over children
// A copy of the tree is returned
// if a filter function returns false on a node, the walk is stopped below that node
// Unit test status: TESTED
function filterTree(tree, filter, children = 'children', newtree = null) {
  if (!filter || !tree) return tree;
  if (filter(tree) || findFirstTreeNode(tree, 'children', 'visible', (node) => node.visible)) {
    const nn = { ...tree };
    delete nn[children];
    if (newtree) {
      if (!newtree[children]) newtree[children] = [];
      newtree[children].push(nn);
    } else {
      newtree = nn;
    }
    if (tree[children] && Array.isArray(tree[children])) {
      tree[children].forEach((ntree) => filterTree(ntree, filter, children, nn));
    }
  }
  return newtree;
}

function filterTreeAlignParent(tree, filter) {
  if (!filter) return tree;
  if (!tree) return null;

  if (filter(tree)) {
    if (tree.children && Array.isArray(tree.children)) {
      const children = tree.children
        .map((ntree) => filterTreeAlignParent(ntree, filter))
        .filter((node) => node);
      tree.children = children?.length > 0 ? children : undefined;
    }
  } else {
    tree = null;
  }

  return tree;
}

function flattenTreeField(tree, field, children, accum) {
  accum.push(tree[field]);

  if (tree[children] && Array.isArray(tree[children])) {
    tree[children].forEach((ntree) => flattenTreeField(ntree, field, children, accum));
  }
  return accum;
}

// Return a flat array of field values for nodes that pass test filter
function flattenFilteredTreeField(
  tree,
  field,
  filter = (node) => true,
  children = 'children',
  accum = []
) {
  if (!field || !tree) return tree;

  if (filter(tree)) accum.push(tree[field]);

  if (tree[children] && Array.isArray(tree[children])) {
    tree[children].forEach((ntree) =>
      flattenFilteredTreeField(ntree, field, filter, children, accum)
    );
  }
  return accum;
}

// Return a flat array of field values for nodes that pass test filter
// filter looks at both the current node and it's parent
function flattenParentFilteredTreeField(
  tree,
  field,
  filter = (node, parent) => true,
  children = 'children',
  accum = [],
  parent = null
) {
  if (!field || !tree) return tree;
  if (filter(tree, parent)) accum.push(tree[field]);

  if (tree[children] && Array.isArray(tree[children])) {
    tree[children].forEach((ntree) =>
      flattenParentFilteredTreeField(ntree, field, filter, children, accum, tree)
    );
  }
  return accum;
}

function transformTreeNodes(tree, children, transform) {
  transform(tree);

  if (tree[children] && Array.isArray(tree[children])) {
    tree[children].forEach((ntree) => transformTreeNodes(ntree, children, transform));
  }
}

function arrayTreeField(tree, field, children, accum, cat = null) {
  if (cat) cat.push(tree[field]);
  else cat = tree[field] ? [tree[field]] : []; // eslint-disable-line no-param-reassign

  if (tree[children] && tree[children].length && Array.isArray(tree[children])) {
    tree[children].forEach((ntree) => {
      arrayTreeField(ntree, field, children, accum, cat.slice());
    });
  } else {
    accum.push(cat);
  }
  return accum;
}

function treeToList(tocPage) {
  const tocList = [];

  const pushToList = (node) => {
    if (!node) {
      return;
    }

    tocList.push(node);

    if (node.children && node.children.length > 0) {
      // eslint-disable-next-line no-restricted-syntax
      for (const child of node.children) {
        pushToList(child);
      }
    }
  };

  pushToList(tocPage);

  return tocList;
}

/**
 * Truncates a tree to a maximum depth
 * @param {Object} tree - The tree to truncate
 * @param {Number} maxDepth - Maximum depth to include (0-indexed, root is depth 0)
 * @param {Array} excludeTruncate - Array of paths to exclude from truncation
 * @param {Number} currentDepth - Current depth in recursion (used internally)
 * @param {String} childrenField - Field name containing child nodes
 * @returns {Object} New truncated tree
 */
function truncateTreeDepth(
  tree,
  maxDepth,
  excludeTruncate = [],
  currentDepth = 0,
  childrenField = 'children'
) {
  if (!tree) return null;

  // Create a copy of the current node
  const nodeCopy = { ...tree };

  // If we've hit max depth, remove all children
  const isExcluded = excludeTruncate.includes(nodeCopy.path);

  if (currentDepth >= maxDepth && !isExcluded) {
    const hasChildren =
      tree[childrenField] && Array.isArray(tree[childrenField]) && tree[childrenField].length > 0;
    if (hasChildren) {
      nodeCopy[childrenField] = [];
    } else {
      delete nodeCopy[childrenField];
    }

    return nodeCopy;
  }

  // If we have children and haven't hit max depth, process them recursively
  if (tree[childrenField] && Array.isArray(tree[childrenField])) {
    nodeCopy[childrenField] = tree[childrenField].map((child) =>
      truncateTreeDepth(child, maxDepth, excludeTruncate, currentDepth + 1, childrenField)
    );
  }

  return nodeCopy;
}

// Helper function to find a node with matching path
function findNodeByPath(nodes, targetPath, pathField = 'path') {
  if (!Array.isArray(nodes)) return null;

  let result = null;

  nodes.some((node) => {
    if (node[pathField] === targetPath) {
      result = node;
      return true; // Stops iteration early
    }

    if (node.children && node.children.length > 0) {
      result = findNodeByPath(node.children, targetPath);
      return !!result; // Stops iteration if found
    }

    return false;
  });

  return result;
}

export default {
  transformTreeNodes,
  filterTree,
  findFirstTreeNode,
  flattenFilteredTreeField,
  flattenParentFilteredTreeField,
  flattenTreeField,
  arrayTreeField,
  treeToList,
  filterTreeAlignParent,
  truncateTreeDepth,
  findNodeByPath
};
